import React from 'react';
import { Field, Modal, Spinner } from '../../components/ui';
import { fmtDate, fmtTime, money } from '../../utils/helpers';
import { PAYMENT_MODES, OTHER_ITEM } from './useServiceDetail';
import { isPerUnit } from '../services/useServices';

// Mobile service detail: facts card, tap-to-tick list of dishes/jobs, payments
// in a sheet. Everything one column, thumb-sized.
export default function ServiceDetailMobile({ vm }) {
  const {
    service, itemsByGroup, items, payments, loading, itemLabel, itemOptions,
    itemsTotal, paidTotal, isVendor, canEdit, canMoney,
    itemForm, setItemF, addItem, savingItem, toggleItemStatus, removeItem,
    payForm, setPayF, addPayment, savingPay, removePayment, payOpen, setPayOpen,
    setConfirmedCount, back,
  } = vm;

  if (loading) return <div className="card card-pad"><Spinner /></div>;
  if (!service) return <div className="card card-pad empty">Service not found. <button className="linklike" onClick={back}>Go back</button></div>;

  const maps = service.GpsLocation
    ? (/^https?:/i.test(service.GpsLocation) ? service.GpsLocation : `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(service.GpsLocation)}`)
    : null;

  return (
    <div>
      <div className="mobile-toolbar">
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <button className="btn light sm" onClick={back}>‹ Back</button>
          <strong style={{ fontSize: 16, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{service.ServiceType}</strong>
        </div>
      </div>

      <div className="card card-pad mobile-record-card">
        <div className="mobile-card-head">
          <div style={{ minWidth: 0 }}>
            <strong className="mobile-title">{service.CompanyName || service.ContactName || '—'}</strong>
            <div className="muted" style={{ fontSize: 12 }}>{service.ContactName || ''}{service.ContactMobile ? ` · ${service.ContactMobile}` : ''}</div>
          </div>
          <div style={{ flex: 1 }} />
          <span className="chip pend">{service.Status}</span>
        </div>
        <div className="mobile-meta">
          <div>{service.FunctionName || 'Whole event'} · {fmtDate(service.FunctionDate || service.ServiceDate)}</div>
          <div>
            🕒 {fmtTime(service.StartTime || service.FunctionStartTime) || '—'}
            {(service.EndTime || service.FunctionEndTime) ? ` – ${fmtTime(service.EndTime || service.FunctionEndTime)}` : ''}
          </div>
          {(service.Venue || service.City) && <div>📍 {[service.Venue, service.VenueAddress, service.City].filter(Boolean).join(', ')}</div>}
          {maps && <div><a href={maps} target="_blank" rel="noreferrer">🗺️ Open in Maps</a></div>}
          <div>
            {isPerUnit(service.RateType)
              ? <>{money(service.Rate)} × {service.ConfirmedCount} = <strong>{money(service.TotalAmount)}</strong></>
              : <>Total <strong>{money(service.TotalAmount)}</strong></>}
          </div>
          <div className="muted">Paid {money(service.AdvancePaid)} · Balance <strong>{money(service.BalanceAmount)}</strong></div>
        </div>
        {service.ContactMobile && (
          <div className="mobile-actions">
            <button className="btn primary sm" onClick={() => { window.location.href = `tel:${String(service.ContactMobile).replace(/\D/g, '')}`; }}>📞 Call</button>
            {canMoney && <button className="btn light sm" onClick={() => setPayOpen(true)}>+ Payment</button>}
          </div>
        )}
      </div>

      {/* The vendor confirms how many plates / units he is doing. */}
      {isVendor && isPerUnit(service.RateType) && (
        <div className="card card-pad" style={{ marginTop: 10 }}>
          <div className="step-label">Confirm the count you will serve</div>
          <div style={{ display: 'flex', gap: 8 }}>
            <input type="number" inputMode="numeric" defaultValue={service.ConfirmedCount}
              id="svc-count" style={{ flex: 1, fontSize: 18, padding: 12, border: '1px solid var(--line)', borderRadius: 10 }} />
            <button className="btn primary" onClick={() => setConfirmedCount(document.getElementById('svc-count').value)}>Save</button>
          </div>
        </div>
      )}

      {/* ── Dishes / jobs ── */}
      <div className="step-label" style={{ marginTop: 12 }}>
        {itemLabel} list · {items.length}{itemsTotal ? ` · ${money(itemsTotal)}` : ''}
      </div>

      {canEdit && (
        <div className="card card-pad" style={{ marginBottom: 10 }}>
          <Field label={`Add ${itemLabel.toLowerCase()}`} type="select" value={itemForm.ItemTypeId} options={itemOptions}
            onChange={(v) => setItemF('ItemTypeId', v)} />
          {itemForm.ItemTypeId === OTHER_ITEM && (
            <Field label="New name" value={itemForm.ItemName} onChange={(v) => setItemF('ItemName', v)} />
          )}
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
            <Field label="Qty" type="number" value={itemForm.Quantity} onChange={(v) => setItemF('Quantity', v)} />
            <Field label="Rate (₹)" type="number" value={itemForm.Rate} onChange={(v) => setItemF('Rate', v)} />
          </div>
          <button className="btn primary" style={{ width: '100%', padding: 12 }} disabled={savingItem} onClick={addItem}>
            {savingItem ? 'Adding…' : `+ Add ${itemLabel.toLowerCase()}`}
          </button>
        </div>
      )}

      {items.length === 0 ? (
        <div className="card card-pad empty">Nothing added yet.</div>
      ) : itemsByGroup.map(({ group, list }) => (
        <div key={group || 'x'}>
          {group && <div className="step-label">{group}</div>}
          <div className="mobile-card-list">
            {list.map(it => (
              <div key={it.ServiceItemId} className="card card-pad mobile-record-card" style={{ opacity: it.Status === 'Done' ? 0.6 : 1 }}>
                <div className="mobile-card-head">
                  <label style={{ display: 'flex', alignItems: 'center', gap: 10, flex: 1, minWidth: 0 }}>
                    <input type="checkbox" checked={it.Status === 'Done'} onChange={() => toggleItemStatus(it)} style={{ width: 22, height: 22 }} />
                    <span style={{ minWidth: 0 }}>
                      <strong className="mobile-title" style={{ textDecoration: it.Status === 'Done' ? 'line-through' : 'none' }}>{it.ItemName}</strong>
                      {(Number(it.Quantity) !== 1 || Number(it.Amount) > 0) && (
                        <div className="muted" style={{ fontSize: 12 }}>
                          {Number(it.Quantity) !== 1 ? `Qty ${it.Quantity}` : ''}{Number(it.Amount) > 0 ? ` · ${money(it.Amount)}` : ''}
                        </div>
                      )}
                    </span>
                  </label>
                  {canEdit && <button className="btn danger sm" onClick={() => removeItem(it)}>✕</button>}
                </div>
              </div>
            ))}
          </div>
        </div>
      ))}

      {/* ── Payments ── */}
      <div className="step-label" style={{ marginTop: 12 }}>Payments · {money(paidTotal)} of {money(service.TotalAmount)}</div>
      {payments.length === 0 ? (
        <div className="card card-pad empty">No payments recorded.</div>
      ) : (
        <div className="mobile-card-list">
          {payments.map(p => (
            <div key={p.PaymentId} className="card card-pad mobile-record-card">
              <div className="mobile-card-head">
                <div style={{ minWidth: 0 }}>
                  <strong className="mobile-title">{money(p.Amount)}</strong>
                  <div className="muted" style={{ fontSize: 12 }}>{fmtDate(p.PaymentDate)} · {p.PaymentMode}{p.Reference ? ` · ${p.Reference}` : ''}</div>
                </div>
                <div style={{ flex: 1 }} />
                {canMoney && <button className="btn danger sm" onClick={() => removePayment(p)}>✕</button>}
              </div>
            </div>
          ))}
        </div>
      )}
      {canMoney && (
        <button className="btn light" style={{ width: '100%', padding: 12, marginTop: 10 }} onClick={() => setPayOpen(true)}>+ Record payment</button>
      )}

      {payOpen && (
        <Modal
          fullScreen
          title="Record payment"
          onClose={() => setPayOpen(false)}
          footer={<>
            <button className="btn light" onClick={() => setPayOpen(false)}>Cancel</button>
            <button className="btn primary" style={{ flex: 1, padding: 12 }} disabled={savingPay} onClick={addPayment}>
              {savingPay ? 'Saving…' : 'Save payment'}
            </button>
          </>}
        >
          <Field label="Date" type="date" value={payForm.PaymentDate} onChange={(v) => setPayF('PaymentDate', v)} />
          <label className="field">Amount (₹)
            <input type="number" inputMode="decimal" value={payForm.Amount} style={{ fontSize: 20, padding: 12 }}
              onChange={(e) => setPayF('Amount', e.target.value)} />
          </label>
          <Field label="Mode" type="select" value={payForm.PaymentMode} options={PAYMENT_MODES} onChange={(v) => setPayF('PaymentMode', v)} />
          <Field label="Reference" value={payForm.Reference} onChange={(v) => setPayF('Reference', v)} />
          <Field label="Remarks" value={payForm.Remarks} onChange={(v) => setPayF('Remarks', v)} />
        </Modal>
      )}
    </div>
  );
}
